import React, { useState, useEffect, useCallback } from 'react';
import {
  Card, Statistic, Table, Tabs, Tag, Space, Button,
  Modal, Form, Input, Select, InputNumber, Switch, Drawer,
  Popconfirm, Tooltip, Badge, Descriptions, Row, Col, Alert, Image, App
} from 'antd';
import {
  UserOutlined, VideoCameraOutlined, PictureOutlined, DollarOutlined,
  EditOutlined, DeleteOutlined, PlusOutlined, SettingOutlined,
  CheckCircleOutlined, CloseCircleOutlined, CrownOutlined,
  SaveOutlined, ReloadOutlined, SearchOutlined, EyeOutlined,
  TeamOutlined, ApiOutlined, SafetyCertificateOutlined, DashboardOutlined,
  ArrowLeftOutlined, HomeOutlined, DownloadOutlined, IdcardOutlined,
  LinkOutlined, DisconnectOutlined, ClearOutlined
} from '@ant-design/icons';
import { useNavigate } from 'react-router-dom';
import { apiClient } from '@/services/api';
import { useAuthStore } from '@/store/authStore';
import { motion } from 'framer-motion';
import { on as onEvent } from '@/services/events';
import SystemConfigEditor from '@/components/Admin/SystemConfigEditor';
import SystemStatusMonitor from '@/components/Admin/SystemStatusMonitor';
import PaymentConfigManager from '@/components/Admin/PaymentConfigManager';
import APIConfigManager from '@/components/Admin/APIConfigManager';
import StorageConfigManager from '@/components/Admin/StorageConfigManager';
import DatabaseManager from '@/components/Admin/DatabaseManager';
import AdminSidebar from '@/components/Admin/AdminSidebar';
import InspirationManagement from '@/pages/admin/InspirationManagement';
import CouponManagement from '@/components/Admin/CouponManagement';
import AgentManagement from '@/components/Admin/AgentManagement';
import CharacterManagement from '@/components/Admin/CharacterManagement';
import { getProviderName } from '@/utils/providerMapping';

const { Option } = Select;
const { TextArea } = Input;

const AdminDashboard: React.FC = () => {
  const { message } = App.useApp();
  const { user } = useAuthStore();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);
  const [stats, setStats] = useState<any>(null);
  const [users, setUsers] = useState<any[]>([]);
  const [generations, setGenerations] = useState<any[]>([]);
  const [genSummary, setGenSummary] = useState<{ videoTotal: number; imageTotal: number; allCount: number } | null>(null);
  const [payments, setPayments] = useState<any[]>([]);
  const [systemConfigs, setSystemConfigs] = useState<any[]>([]);
  
  // 当前活动菜单项
  const [activeMenu, setActiveMenu] = useState('overview');
  
  // 侧边栏折叠状态
  const [sidebarCollapsed, setSidebarCollapsed] = useState(false);
  
  // 筛选条件
  const [generationType, setGenerationType] = useState<string>('all');
  const [generationStatus, setGenerationStatus] = useState<string>('all');
  const [generationUserId, setGenerationUserId] = useState<string | undefined>(undefined);
  const [genPage, setGenPage] = useState<number>(1);
  const [genLimit, setGenLimit] = useState<number>(10);
  const [genTotal, setGenTotal] = useState<number>(0);
  const [paymentType, setPaymentType] = useState<string>('all');
  const [paymentStatus, setPaymentStatus] = useState<string>('all');
  
  // 模态框状态
  const [editUserModal, setEditUserModal] = useState(false);
  const [editConfigModal, setEditConfigModal] = useState(false);
  const [userDetailDrawer, setUserDetailDrawer] = useState(false);
  const [selectedUser, setSelectedUser] = useState<any>(null);
  const [selectedConfig, setSelectedConfig] = useState<any>(null);
  const [bindAgentModal, setBindAgentModal] = useState(false);
  const [bindAgentUser, setBindAgentUser] = useState<any>(null);
  const [agentOptions, setAgentOptions] = useState<any[]>([]);
  const [bindAgentLoading, setBindAgentLoading] = useState(false);
  
  // 预览Modal状态
  const [previewModal, setPreviewModal] = useState({
    visible: false,
    type: 'image' as 'image' | 'video',
    url: '',
    urls: [] as string[],
    prompt: ''
  });
  
  const [form] = Form.useForm();
  const [configForm] = Form.useForm();
  const [bindAgentForm] = Form.useForm();

  const [userSearchInput, setUserSearchInput] = useState('');
  const [userSearch, setUserSearch] = useState('');
  const [userRoleFilter, setUserRoleFilter] = useState<string>('all');
  const [userStatusFilter, setUserStatusFilter] = useState<string>('all');

  // 检查管理员权限
  if (user?.role !== 'ADMIN') {
    return (
      <div className="container mx-auto px-4 py-8">
        <Card>
          <div className="text-center py-12">
            <CloseCircleOutlined className="text-6xl text-red-500 mb-4" />
            <h2 className="text-2xl font-bold text-red-500 mb-4">⛔ 访问被拒绝</h2>
            <p className="text-gray-600">你没有权限访问管理后台</p>
            <p className="text-sm text-gray-400 mt-2">当前角色: {user?.role || '未登录'}</p>
          </div>
        </Card>
      </div>
    );
  }
  
  // 全局事件：支付/生成/用户/积分变更后自动刷新相关数据块
  useEffect(() => {
    const trigger = () => setRefreshTick((v) => v + 1);
    onEvent('payment_updated', trigger);
    onEvent('video_updated', trigger);
    onEvent('image_updated', trigger);
    onEvent('user_updated', trigger);
    onEvent('credits_updated', trigger);
  }, []);

  const [refreshTick, setRefreshTick] = useState(0);

  const fetchStats = async () => {
    try {
      setLoading(true);
      // 🔥 统计数据查询可能需要更长时间，增加超时时间到60秒
      const response = await apiClient.get('/admin/stats', {
        timeout: 60000 // 60秒超时
      });
      if (response.success) {
        setStats(response.data);
      }
    } catch (error: any) {
      message.error('获取统计数据失败: ' + (error.response?.data?.message || error.message));
    } finally {
      setLoading(false);
    }
  };

  // 获取用户列表
  const fetchUsers = useCallback(async () => {
    try {
      setLoading(true);
      const params = new URLSearchParams();
      params.set('page', '1');
      params.set('limit', '50');

      const trimmedSearch = userSearch.trim();
      if (trimmedSearch) {
        params.set('search', trimmedSearch);
      }

      if (userRoleFilter && userRoleFilter !== 'all') {
        params.set('role', userRoleFilter);
      }

      if (userStatusFilter && userStatusFilter !== 'all') {
        params.set('status', userStatusFilter);
      }

      const response = await apiClient.get(`/admin/users?${params.toString()}`);
      if (response.success) {
        setUsers(response.data.users || []);
      }
    } catch (error: any) {
      message.error('获取用户列表失败: ' + (error.response?.data?.message || error.message));
    } finally {
      setLoading(false);
    }
  }, [userSearch, userRoleFilter, userStatusFilter]);

  const refreshUserDetail = async (userId: string) => {
    try {
      const response = await apiClient.get(`/admin/users/${userId}`);
      if (response.success) {
        setSelectedUser(response.data.user);
      }
    } catch (error) {
      console.error('刷新用户详情失败', error);
    }
  };

  const loadAgentOptions = async () => {
    try {
      setBindAgentLoading(true);
      const response = await apiClient.get('/admin/agents');
      if (response.success) {
        setAgentOptions(response.data || []);
      }
    } catch (error: any) {
      message.error('加载代理商列表失败: ' + (error.response?.data?.message || error.message));
    } finally {
      setBindAgentLoading(false);
    }
  };

  const handleOpenBindAgent = async (userRecord: any) => {
    setBindAgentUser(userRecord);
    setBindAgentModal(true);
    bindAgentForm.resetFields();
    await loadAgentOptions();
  };

  const handleBindAgent = async () => {
    try {
      const values = await bindAgentForm.validateFields();
      if (!bindAgentUser) return;
      setBindAgentLoading(true);
      await apiClient.post(`/admin/agents/${values.agentId}/users/${bindAgentUser.id}`, {
        role: values.role || 'AGENT_ADMIN'
      });
      message.success('绑定代理商成功');
      setBindAgentModal(false);
      setBindAgentUser(null);
      await fetchUsers();
      if (selectedUser?.id === bindAgentUser.id) {
        refreshUserDetail(bindAgentUser.id);
      }
    } catch (error: any) {
      if (error?.errorFields) {
        return;
      }
      message.error('绑定失败: ' + (error.response?.data?.message || error.message));
    } finally {
      setBindAgentLoading(false);
    }
  };

  const handleUnbindAgent = async (userRecord: any) => {
    if (!userRecord?.agentId) {
      message.warning('该用户未绑定代理商');
      return;
    }
    try {
      await apiClient.delete(`/admin/agents/${userRecord.agentId}/users/${userRecord.id}`);
      message.success('已解除代理商绑定');
      await fetchUsers();
      if (selectedUser?.id === userRecord.id) {
        refreshUserDetail(userRecord.id);
      }
    } catch (error: any) {
      message.error('解绑失败: ' + (error.response?.data?.message || error.message));
    }
  };

  // 获取生成记录
  const fetchGenerations = async (
    opts?: { type?: string; status?: string; userId?: string | undefined; page?: number; limit?: number }
  ) => {
    try {
      setLoading(true);
      console.log('📥 [管理后台] 开始加载生成记录...');
      const type = (opts?.type ?? generationType) === 'all' ? 'all' : (opts?.type ?? generationType);
      const status = (opts?.status ?? generationStatus);
      const page = opts?.page ?? genPage;
      const limit = opts?.limit ?? genLimit;
      const userId = opts?.userId ?? generationUserId;

      const params = new URLSearchParams();
      params.set('type', type);
      if (status && status !== 'all') params.set('status', status);
      if (userId) params.set('userId', userId);
      params.set('page', String(page));
      params.set('limit', String(limit));

      const response = await apiClient.get(`/admin/generations?${params.toString()}`);
      console.log('✅ [管理后台] 生成记录响应:', response);
      
      if (response.success) {
        const gens = response.data.items || response.data.generations || [];
        console.log(`📊 [管理后台] 获取到 ${gens.length} 条记录`);
        
        // 调试：检查图片链接
        gens.forEach((g: any, index: number) => {
          if (g.outputImages) {
            console.log(`  图片记录 #${index + 1}:`, {
              id: g.id,
              status: g.status,
              imageCount: g.outputImages.length,
              firstImage: g.outputImages[0]?.substring(0, 100) + '...'
            });
          }
        });
        
        setGenerations(gens);
        const p = response.data.pagination || {};
        setGenPage(Number(p.page) || page);
        setGenLimit(Number(p.limit) || limit);
        setGenTotal(Number(p.total) || gens.length);
        if (response.data.summary) {
          const s = response.data.summary;
          setGenSummary({
            videoTotal: Number(s.videoTotal) || 0,
            imageTotal: Number(s.imageTotal) || 0,
            allCount: Number(s.allCount) || ((Number(s.videoTotal) || 0) + (Number(s.imageTotal) || 0))
          });
        } else {
          setGenSummary(null);
        }
      }
    } catch (error: any) {
      console.error('❌ [管理后台] 获取生成记录失败:', error);
      message.error('获取生成记录失败');
    } finally {
      setLoading(false);
    }
  };

  // 获取支付记录
  const fetchPayments = async () => {
    try {
      setLoading(true);
      const response = await apiClient.get('/admin/payments?page=1&limit=30');
      if (response.success) {
        setPayments(response.data.payments || []);
      }
    } catch (error: any) {
      message.error('获取支付记录失败');
    } finally {
      setLoading(false);
    }
  };

  // 首次加载与事件触发后的自动刷新
  useEffect(() => {
    fetchStats();
    fetchGenerations({ page: 1 });
    fetchPayments();
  }, [refreshTick]);

  useEffect(() => {
    fetchUsers();
  }, [fetchUsers, refreshTick]);

  // 增量合并：尽量避免整表刷新（无服务器分页依赖时有效）
  useEffect(() => {
    const mergeById = (list: any[], patch: any, key: string = 'id') => {
      const idx = list.findIndex((i: any) => i[key] === patch[key])
      if (idx >= 0) {
        const next = list.slice()
        next[idx] = { ...next[idx], ...patch }
        return next
      }
      return [patch, ...list]
    }

    const onPayment = (p: any) => setPayments(prev => mergeById(prev, p))
    const onVideo = (p: any) => setGenerations(prev => mergeById(prev, { ...p, type: 'video' }))
    const onImage = (p: any) => setGenerations(prev => mergeById(prev, { ...p, type: 'image' }))
    const onUser = (p: any) => setUsers(prev => mergeById(prev, p))
    const onCredits = (p: any) => {
      // 积分更新事件,更新对应用户的积分
      if (p.userId) {
        setUsers(prev => mergeById(prev, { id: p.userId, credits: p.credits }))
      }
    }

    onEvent('payment_updated', onPayment)
    onEvent('video_updated', onVideo)
    onEvent('image_updated', onImage)
    onEvent('user_updated', onUser)
    onEvent('credits_updated', onCredits)
  }, [])

  // 编辑用户
  const handleEditUser = async (values: any) => {
    try {
      setLoading(true);
      const { id, role, credits, isActive, agentId } = values;
      const previousAgentId = selectedUser?.agent?.id || null;

      if ((agentId || null) !== previousAgentId) {
        if (agentId) {
          await apiClient.post(`/admin/agents/${agentId}/users/${id}`, { role: role || selectedUser.role });
        } else if (previousAgentId) {
          await apiClient.delete(`/admin/agents/${previousAgentId}/users/${id}`);
        }
      }

      if (role && role !== selectedUser.role) {
        await apiClient.patch(`/admin/users/${id}/role`, { role });
      }

      if (isActive !== undefined && isActive !== selectedUser.isActive) {
        await apiClient.patch(`/admin/users/${id}/status`, { isActive });
      }

      if (credits !== undefined && credits !== selectedUser.credits) {
        await apiClient.patch(`/admin/users/${id}/credits`, { 
          credits,
          reason: '管理员调整'
        });
      }
      
      message.success('用户信息更新成功');
      setEditUserModal(false);
      fetchUsers();
      if (selectedUser?.id === id) {
        refreshUserDetail(id);
      }
    } catch (error: any) {
      message.error('更新失败: ' + (error.response?.data?.message || error.message));
    } finally {
      setLoading(false);
    }
  };

  // 查看用户详情
  const handleViewUser = async (userId: string) => {
    try {
      setLoading(true);
      const response = await apiClient.get(`/admin/users/${userId}`);
      if (response.success) {
        setSelectedUser(response.data.user);
        setUserDetailDrawer(true);
      }
    } catch (error: any) {
      message.error('获取用户详情失败');
    } finally {
      setLoading(false);
    }
  };

  // 切换用户状态
  const handleToggleUserStatus = async (userId: string, currentStatus: boolean) => {
    try {
      await apiClient.patch(`/admin/users/${userId}/status`, { isActive: !currentStatus });
      message.success(`用户已${!currentStatus ? '激活' : '禁用'}`);
      fetchUsers();
    } catch (error: any) {
      message.error('操作失败');
    }
  };

  useEffect(() => {
    fetchStats();
    fetchUsers();
  }, []);

  const roleDisplayMap: Record<string, { color: string; icon: React.ReactNode; label: string }> = {
    ADMIN: { color: 'red', icon: <CrownOutlined />, label: '管理员' },
    MODERATOR: { color: 'orange', icon: <SafetyCertificateOutlined />, label: '协管员' },
    AGENT_ADMIN: { color: 'purple', icon: <IdcardOutlined />, label: '代理商管理员' },
    USER: { color: 'blue', icon: <UserOutlined />, label: '普通用户' }
  };

  const renderRoleTag = (role?: string) => {
    const key = (role || '').toUpperCase();
    const info = roleDisplayMap[key] || {
      color: 'default',
      icon: <UserOutlined />,
      label: role || '未知角色'
    };
    return (
      <Tag icon={info.icon} color={info.color}>
        {info.label}
      </Tag>
    );
  };

  // 用户表格列
  const userColumns = [
    {
      title: '邮箱/手机',
      dataIndex: 'email',
      key: 'email',
      width: 220,
      ellipsis: true,
      render: (email: string, record: any) => {
        if (record.loginMethod === 'PHONE' && record.phone) {
          return <span>{record.phone}</span>;
        }
        return <span>{email || '-'}</span>;
      },
    },
    {
      title: '用户名',
      dataIndex: 'username',
      key: 'username',
      width: 120,
      render: (username: string) => (
        <span className="font-medium text-gray-800">{username}</span>
      )
    },
    {
      title: '角色',
      key: 'role',
      width: 200,
      render: (_: any, record: any) => (
        <Space direction="vertical" size={4} className="py-1">
          {renderRoleTag(record.role)}
          {record.agent && (
            <Space size="small">
              <TeamOutlined className="text-purple-500" />
              <span className="text-sm text-purple-600">{record.agent.name}</span>
              {record.agent.code && <Tag color="purple">{record.agent.code}</Tag>}
            </Space>
          )}
        </Space>
      ),
    },
    {
      title: '当前积分',
      dataIndex: 'pointsBalance',
      key: 'pointsBalance',
      width: 100,
      align: 'right' as const,
      render: (_: any, record: any) => {
        const current = record.pointsBalance ?? record.credits ?? 0;
        return <span className="font-semibold text-green-600">{current}</span>;
      },
    },
    {
      title: '已消耗积分',
      key: 'totalCreditsUsed',
      width: 110,
      align: 'right' as const,
      render: (_: any, record: any) => {
        const used = record.totalCreditsUsed ?? record.creationStats?.totalCreditsUsed ?? 0;
        return <span className="text-gray-600">{used}</span>;
      },
    },
    {
      title: '状态',
      dataIndex: 'isActive',
      key: 'isActive',
      width: 80,
      render: (isActive: boolean, record: any) => (
        <Popconfirm
          title={`确定要${isActive ? '禁用' : '激活'}该用户吗？`}
          onConfirm={() => handleToggleUserStatus(record.id, isActive)}
          okText="确定"
          cancelText="取消"
        >
          <Tag 
            icon={isActive ? <CheckCircleOutlined /> : <CloseCircleOutlined />}
            color={isActive ? 'success' : 'error'}
            className="cursor-pointer"
          >
            {isActive ? '激活' : '禁用'}
          </Tag>
        </Popconfirm>
      ),
    },
    {
      title: '注册时间',
      dataIndex: 'createdAt',
      key: 'createdAt',
      width: 160,
      render: (date: string) => new Date(date).toLocaleDateString('zh-CN'),
    },
    {
      title: '操作',
      key: 'actions',
      width: 160,
      fixed: 'right' as const,
      render: (_: any, record: any) => (
        <Space size="small">
          <Tooltip title="查看详情">
            <Button
              type="link"
              size="small"
              icon={<EyeOutlined />}
              onClick={() => handleViewUser(record.id)}
            >
              详情
            </Button>
          </Tooltip>
          <Tooltip title="编辑用户">
            <Button
              type="link"
              size="small"
              icon={<EditOutlined />}
              onClick={() => {
                setSelectedUser(record);
                loadAgentOptions();
                form.setFieldsValue({
                  id: record.id,
                  email: record.email,
                  username: record.username,
                  role: record.role,
                  credits: record.credits,
                  isActive: record.isActive,
                  agentId: record.agent?.id || null
                });
                setEditUserModal(true);
              }}
            >
              编辑
            </Button>
          </Tooltip>
        </Space>
      ),
    },
  ];

  // 处理预览（视频和图片都使用Modal，底部有明显的关闭按钮）
  const handlePreview = (record: any) => {
    console.log('🔍 [管理后台] 预览记录:', record);
    
    const isVideo = !record.outputImages;
    
    if (isVideo) {
      // 视频预览
      console.log('📹 [管理后台] 视频URL:', record.outputVideo);
      if (record.outputVideo) {
        setPreviewModal({
          visible: true,
          type: 'video',
          url: record.outputVideo,
          urls: [],
          prompt: record.prompt || '未命名作品'
        });
      } else {
        message.warning('视频文件不存在');
      }
    } else {
      // 图片预览 - 使用自定义Modal
      console.log('🖼️ [管理后台] 图片URLs:', record.outputImages);
      
      // 处理outputImages可能是字符串的情况
      let imageUrls = record.outputImages;
      if (typeof imageUrls === 'string') {
        try {
          imageUrls = JSON.parse(imageUrls);
        } catch (e) {
          console.error('❌ [预览] JSON解析失败:', imageUrls);
          imageUrls = [];
        }
      }
      
      // 确保是数组并过滤空链接
      if (!Array.isArray(imageUrls)) {
        imageUrls = [];
      }
      
      const validUrls = imageUrls.filter((url: string) => url && url.trim() !== '');
      
      if (validUrls.length === 0) {
        message.warning('图片链接无效或为空');
        return;
      }
      
      console.log('✅ [管理后台] 有效图片URLs:', validUrls);
      
      setPreviewModal({
        visible: true,
        type: 'image',
        url: validUrls[0],
        urls: validUrls,
        prompt: record.prompt || '未命名作品'
      });
    }
  };

  // 生成记录列（参考HistoryPage的实现）
  const generationColumns = [
    {
      title: '预览',
      key: 'preview',
      width: 100,
      render: (_: any, record: any) => {
        const isVideo = !record.outputImages;
        const hasContent = isVideo ? record.outputVideo : (record.outputImages && record.outputImages.length > 0);
        
        if (!hasContent || record.status !== 'COMPLETED') {
          return (
            <div className="w-20 h-20 bg-gray-100 rounded-lg flex items-center justify-center">
              {isVideo ? <VideoCameraOutlined className="text-2xl text-gray-400" /> : <PictureOutlined className="text-2xl text-gray-400" />}
            </div>
          );
        }
        
        return (
          <div 
            className="relative group cursor-pointer w-20 h-20 overflow-hidden rounded-lg"
            onClick={() => handlePreview(record)}
          >
            {isVideo ? (
              // 视频缩略图 - 使用video标签，悬停播放（参考HistoryPage）
              <video
                className="w-full h-full rounded-lg shadow-sm group-hover:shadow-md transition-all duration-300"
                style={{ 
                  objectFit: 'cover',
                  objectPosition: 'center'
                }}
                src={record.outputVideo}
                preload="metadata"
                muted
                playsInline
                crossOrigin="anonymous"
                onMouseEnter={(e) => {
                  const video = e.currentTarget;
                  video.play().catch(() => {});
                }}
                onMouseLeave={(e) => {
                  const video = e.currentTarget;
                  video.pause();
                  video.currentTime = 0;
                }}
              >
                <source src={record.outputVideo} type="video/mp4" />
              </video>
            ) : (
              // 图片缩略图 - 点击打开自定义Modal预览
              <div className="relative w-full h-full">
                {(() => {
                  // 处理outputImages可能是字符串的情况
                  let imageUrls = record.outputImages;
                  if (typeof imageUrls === 'string') {
                    try {
                      imageUrls = JSON.parse(imageUrls);
                    } catch (e) {
                      imageUrls = [];
                    }
                  }
                  
                  if (!Array.isArray(imageUrls)) {
                    imageUrls = [];
                  }
                  
                  if (imageUrls.length === 0) {
                    return (
                      <div className="w-full h-full bg-gradient-to-br from-purple-100 to-pink-100 flex items-center justify-center rounded-lg">
                        <PictureOutlined className="text-2xl text-purple-400" />
                      </div>
                    );
                  }
                  
                  return (
                    <img
                      src={imageUrls[0]}
                      alt="Generated image"
                      loading="lazy"
                      className="w-full h-full object-cover rounded-lg shadow-sm group-hover:shadow-md transition-all duration-300"
                      onError={(e) => {
                        const target = e.target as HTMLImageElement;
                        target.style.display = 'none';
                        const parent = target.parentElement;
                        if (parent) {
                          parent.innerHTML = `
                            <div class="w-full h-full bg-gradient-to-br from-purple-100 to-pink-100 flex items-center justify-center rounded-lg">
                              <svg class="w-8 h-8 text-purple-400" fill="currentColor" viewBox="0 0 20 20">
                                <path fill-rule="evenodd" d="M4 3a2 2 0 00-2 2v10a2 2 0 002 2h12a2 2 0 002-2V5a2 2 0 00-2-2H4zm12 12H4l4-8 3 6 2-4 3 6z" clip-rule="evenodd" />
                              </svg>
                            </div>
                          `;
                        }
                      }}
                    />
                  );
                })()}
                
                {/* 悬停遮罩层 - 显示眼睛图标 */}
                <div className="absolute inset-0 bg-black bg-opacity-0 group-hover:bg-opacity-30 rounded-lg flex items-center justify-center transition-all duration-300 pointer-events-none">
                  <div className="opacity-0 group-hover:opacity-100 transition-opacity duration-300">
                    <div className="w-8 h-8 bg-white bg-opacity-90 rounded-full flex items-center justify-center shadow-lg">
                      <EyeOutlined className="text-sm text-purple-500" />
                    </div>
                  </div>
                </div>
              </div>
            )}
          </div>
        );
      },
    },
    {
      title: '用户',
      key: 'userEmail',
      width: 100,
      ellipsis: true,
      render: (_: any, record: any) => record.user?.email || record.username || '-'
    },
    {
      title: '类型',
      dataIndex: 'type',
      key: 'type',
      width: 80,
      render: (_: string, record: any) => {
        const isVideo = !record.outputImages;
        return (
          <Tag 
            icon={isVideo ? <VideoCameraOutlined /> : <PictureOutlined />} 
            color={isVideo ? 'blue' : 'purple'}
          >
            {isVideo ? '视频' : '图片'}
          </Tag>
        );
      },
    },
    {
      title: '服务商',
      key: 'provider',
      width: 120,
      render: (_: any, record: any) => {
        const isVideo = !record.outputImages;
        const providerName = record.providerName || getProviderName(record.model || '', isVideo ? 'video' : 'image');
        return providerName ? (
          <Tag color={isVideo ? 'blue' : 'purple'}>{providerName}</Tag>
        ) : '-';
      },
    },
    {
      title: '积分',
      dataIndex: 'cost',
      key: 'cost',
      width: 80,
      align: 'right' as const,
      render: (cost: number | null | undefined) => {
        if (cost === null || cost === undefined || cost === 0) {
          return <span style={{ color: '#999' }}>-</span>;
        }
        return <span style={{ fontWeight: 500, color: '#f59e0b' }}>{cost} 积分</span>;
      },
    },
    {
      title: '创建时间',
      dataIndex: 'createdAt',
      key: 'createdAt',
      width: 120,
      render: (date: string) => new Date(date).toLocaleString('zh-CN'),
    },
    {
      title: '提示词',
      dataIndex: 'prompt',
      key: 'prompt',
      ellipsis: true,
    },
    {
      title: '状态',
      dataIndex: 'status',
      key: 'status',
      width: 100,
      render: (status: string) => {
        const colors: any = {
          PENDING: 'orange',
          PROCESSING: 'blue',
          COMPLETED: 'green',
          FAILED: 'red',
          CANCELLED: 'gray',
        };
        return <Tag color={colors[status]}>{status}</Tag>;
      },
    },
  ];

  // 支付记录列
  const paymentColumns = [
    {
      title: '用户',
      key: 'userEmail',
      width: 180,
      ellipsis: true,
      render: (_: any, record: any) => {
        const user = record.user;
        if (!user) return '-';
        // 优先显示邮箱，如果没有则显示手机号，再没有则显示用户名
        return user.email || user.phone || user.username || '-';
      },
    },
    {
      title: '类型',
      dataIndex: 'type',
      key: 'type',
      width: 100,
      render: (type: string) => <Tag color="blue">{type}</Tag>,
    },
    {
      title: '金额',
      dataIndex: 'amount',
      key: 'amount',
      width: 100,
      render: (amount: number) => `￥${amount.toFixed(2)}`,
    },
    {
      title: '积分',
      dataIndex: 'credits',
      key: 'credits',
      width: 80,
    },
    {
      title: '状态',
      dataIndex: 'status',
      key: 'status',
      width: 100,
      render: (status: string) => {
        const colors: any = {
          PENDING: 'orange',
          COMPLETED: 'green',
          FAILED: 'red',
          REFUNDED: 'gray',
        };
        return <Tag color={colors[status]}>{status}</Tag>;
      },
    },
    {
      title: '时间',
      dataIndex: 'createdAt',
      key: 'createdAt',
      width: 150,
      render: (date: string) => new Date(date).toLocaleString('zh-CN'),
    },
  ];

  // 渲染概览页面（统计卡片）
  const renderOverview = () => (
    <>
      {/* 页面标题 - 美化版 */}
      <div className="mb-6">
        <h2 className="text-3xl font-bold mb-2">
          <span className="bg-gradient-to-r from-blue-600 via-purple-600 to-pink-600 bg-clip-text text-transparent">
            管理控制台概览
          </span>
        </h2>
        <p className="text-gray-600">实时监控系统运行状态和数据统计</p>
      </div>

      {/* 统计卡片 - 渐变美化版 */}
      {stats && (
        <Row gutter={[16, 16]} className="mb-6">
          {/* 总用户数 - 蓝色渐变 */}
          <Col xs={12} sm={12} lg={6}>
            <motion.div 
              whileHover={{ y: -4, scale: 1.01 }} 
              transition={{ duration: 0.25, ease: [0.4, 0, 0.2, 1] }}
            >
              <Card className="border-0 shadow-2xl rounded-2xl overflow-hidden bg-gradient-to-br from-blue-50 via-cyan-50 to-blue-100 hover:shadow-blue-200">
                <Statistic
                  title={<span className="text-gray-700 font-bold text-sm">👥 总用户数</span>}
                  value={stats.totalUsers || 0}
                  valueStyle={{ 
                    fontSize: '2rem',
                    fontWeight: 'bold',
                    background: 'linear-gradient(to right, #2563eb, #06b6d4)',
                    WebkitBackgroundClip: 'text',
                    WebkitTextFillColor: 'transparent'
                  }}
                />
                <div className="mt-3 pt-3 border-t border-blue-200">
                  <span className="text-xs font-medium text-blue-600">活跃用户: </span>
                  <span className="text-xs font-bold text-blue-700">{stats.activeUsers || 0}</span>
                </div>
              </Card>
            </motion.div>
          </Col>

          {/* 视频生成 - 绿色渐变 */}
          <Col xs={12} sm={12} lg={6}>
            <motion.div 
              whileHover={{ y: -4, scale: 1.01 }} 
              transition={{ duration: 0.25, ease: [0.4, 0, 0.2, 1] }}
            >
              <Card className="border-0 shadow-2xl rounded-2xl overflow-hidden bg-gradient-to-br from-emerald-50 via-green-50 to-teal-100 hover:shadow-green-200">
                <Statistic
                  title={<span className="text-gray-700 font-bold text-sm">🎬 视频生成</span>}
                  value={stats.totalVideoGenerations || 0}
                  valueStyle={{ 
                    fontSize: '2rem',
                    fontWeight: 'bold',
                    background: 'linear-gradient(to right, #10b981, #14b8a6)',
                    WebkitBackgroundClip: 'text',
                    WebkitTextFillColor: 'transparent'
                  }}
                />
                <div className="mt-3 pt-3 border-t border-green-200">
                  <span className="text-xs font-medium text-green-600">本月生成: </span>
                  <span className="text-xs font-bold text-green-700">{stats.monthlyVideos || 0}</span>
                </div>
              </Card>
            </motion.div>
          </Col>

          {/* 图片生成 - 橙色渐变 */}
          <Col xs={12} sm={12} lg={6}>
            <motion.div 
              whileHover={{ y: -4, scale: 1.01 }} 
              transition={{ duration: 0.25, ease: [0.4, 0, 0.2, 1] }}
            >
              <Card className="border-0 shadow-2xl rounded-2xl overflow-hidden bg-gradient-to-br from-amber-50 via-orange-50 to-yellow-100 hover:shadow-orange-200">
                <Statistic
                  title={<span className="text-gray-700 font-bold text-sm">🎨 图片生成</span>}
                  value={stats.totalImageGenerations || 0}
                  valueStyle={{ 
                    fontSize: '2rem',
                    fontWeight: 'bold',
                    background: 'linear-gradient(to right, #f59e0b, #f97316)',
                    WebkitBackgroundClip: 'text',
                    WebkitTextFillColor: 'transparent'
                  }}
                />
                <div className="mt-3 pt-3 border-t border-orange-200">
                  <span className="text-xs font-medium text-orange-600">本月生成: </span>
                  <span className="text-xs font-bold text-orange-700">{stats.monthlyImages || 0}</span>
                </div>
              </Card>
            </motion.div>
          </Col>



          {/* 总收入 - 红色渐变 */}
          <Col xs={12} sm={12} lg={6}>
            <motion.div 
              whileHover={{ y: -4, scale: 1.01 }} 
              transition={{ duration: 0.25, ease: [0.4, 0, 0.2, 1] }}
            >
              <Card className="border-0 shadow-2xl rounded-2xl overflow-hidden bg-gradient-to-br from-rose-50 via-pink-50 to-red-100 hover:shadow-red-200">
                <Statistic
                  title={<span className="text-gray-700 font-bold text-sm">💰 总收入</span>}
                  value={stats.totalRevenue || 0}
                  prefix="￥"
                  precision={2}
                  valueStyle={{ 
                    fontSize: '2rem',
                    fontWeight: 'bold',
                    background: 'linear-gradient(to right, #ef4444, #ec4899)',
                    WebkitBackgroundClip: 'text',
                    WebkitTextFillColor: 'transparent'
                  }}
                />
                <div className="mt-3 pt-3 border-t border-red-200">
                  <span className="text-xs font-medium text-red-600">本月收入: </span>
                  <span className="text-xs font-bold text-red-700">￥{typeof stats.monthlyRevenue === 'number' ? stats.monthlyRevenue.toFixed(2) : '0.00'}</span>
                </div>
              </Card>
            </motion.div>
          </Col>
        </Row>
      )}

      {/* 快捷操作按钮 */}
      <Card className="mb-6 shadow-lg rounded-xl border-0 bg-gradient-to-r from-gray-50 to-blue-50">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-2">
            <span className="text-lg">⚡</span>
            <span className="font-bold text-gray-700">快捷操作</span>
          </div>
          <Space wrap>
            <Button 
              icon={<ReloadOutlined />} 
              onClick={() => {
                fetchStats();
                fetchUsers();
                fetchGenerations();
                fetchPayments();
                message.success('数据已刷新');
              }}
            >
              刷新全部数据
            </Button>
            <Button 
              icon={<SettingOutlined />} 
              type="primary"
              onClick={() => setActiveMenu('settings')}
            >
              系统设置
            </Button>
            <Button 
              icon={<HomeOutlined />} 
              onClick={() => navigate('/')}
            >
              返回首页
            </Button>
          </Space>
        </div>
      </Card>
    </>
  );

  // 根据活动菜单渲染内容
  const renderContent = () => {
    switch (activeMenu) {
      case 'overview':
        return renderOverview();
      case 'users':
        return renderUsersContent();
      case 'generations':
        return renderGenerationsContent();
      case 'payments':
        return renderPaymentsContent();
      case 'settings':
        return (
          <>
            {/* 页面标题 - 美化版 */}
            <div className="mb-6">
              <h2 className="text-3xl font-bold mb-2">
                <span className="bg-gradient-to-r from-orange-600 via-red-600 to-pink-600 bg-clip-text text-transparent">
                  ⚙️ 系统配置
                </span>
              </h2>
              <p className="text-gray-600">管理站点信息、Logo和系统参数设置</p>
            </div>

            <Card className="shadow-xl rounded-2xl border-0">
              <Alert
                message="⚠️ 系统配置管理"
                description="管理系统全局配置，修改后立即生效。请谨慎操作，错误的配置可能影响系统运行。"
                type="warning"
                showIcon
                closable
                className="mb-6"
              />
              <SystemConfigEditor onSave={() => message.success('系统配置已更新')} />
            </Card>
          </>
        );
      case 'status':
        return <SystemStatusMonitor />;
      case 'payment-config':
        return <PaymentConfigManager />;
      case 'api-config':
        return <APIConfigManager />;
      case 'storage-config':
        return <StorageConfigManager />;
      case 'database':
        return <DatabaseManager />;
      case 'inspiration':
        return <InspirationManagement />;
      case 'characters':
        return <CharacterManagement />;
      case 'coupons':
        return <CouponManagement />;
      case 'agents':
        return <AgentManagement />;
      default:
        return renderOverview();
    }
  };

  // 渲染用户管理内容
  const renderUsersContent = () => (
    <>
      {/* 页面标题 - 美化版 */}
      <div className="flex justify-between items-center mb-6">
        <div>
          <h2 className="text-3xl font-bold mb-2">
            <span className="bg-gradient-to-r from-blue-600 via-indigo-600 to-purple-600 bg-clip-text text-transparent">
              👥 用户管理
            </span>
          </h2>
          <p className="text-gray-600">管理用户账户、权限和积分</p>
        </div>
        <Button 
          type="primary"
          icon={<ReloadOutlined />}
          onClick={async () => {
            await fetchUsers();
            message.success('用户数据已刷新');
          }}
          size="large"
        >
          刷新数据
        </Button>
      </div>

      <Card className="shadow-xl rounded-2xl border-0">
        <Alert
          message="用户管理说明"
          description="可以查看、编辑用户信息，调整用户角色和积分，激活或禁用用户账户"
          type="info"
          showIcon
          closable
          className="mb-4"
        />

        <div className="mb-6 px-4 py-4 bg-gray-50 rounded-xl border border-gray-100">
          <div className="flex flex-col lg:flex-row lg:items-center lg:gap-4 gap-3">
            <Input.Search
              value={userSearchInput}
              allowClear
              placeholder="搜索邮箱、用户名或手机号"
              onChange={(e) => {
                const value = e.target.value;
                setUserSearchInput(value);
                if (value === '') {
                  setUserSearch('');
                }
              }}
              onSearch={(value) => {
                const trimmed = value.trim();
                setUserSearchInput(trimmed);
                setUserSearch(trimmed);
              }}
              className="flex-1"
            />
            <div className="flex flex-col sm:flex-row sm:items-center gap-3 flex-none">
              <Select
                value={userRoleFilter}
                onChange={(value) => setUserRoleFilter(value)}
                style={{ minWidth: 160 }}
                placeholder="筛选角色"
              >
                <Option value="all">全部角色</Option>
                {Object.keys(roleDisplayMap).map((key) => (
                  <Option value={key} key={key}>
                    {roleDisplayMap[key].label}
                  </Option>
                ))}
              </Select>
              <Select
                value={userStatusFilter}
                onChange={(value) => setUserStatusFilter(value)}
                style={{ minWidth: 140 }}
                placeholder="筛选状态"
              >
                <Option value="all">全部状态</Option>
                <Option value="active">仅激活</Option>
                <Option value="inactive">已禁用</Option>
              </Select>
            </div>
            <div className="flex gap-3 flex-none">
              <Button
                icon={<ReloadOutlined />}
                onClick={async () => {
                  await fetchUsers();
                  message.success('筛选结果已刷新');
                }}
              >
                应用筛选
              </Button>
              <Button
                icon={<ClearOutlined />}
                onClick={() => {
                  setUserSearchInput('');
                  setUserSearch('');
                  setUserRoleFilter('all');
                  setUserStatusFilter('all');
                  message.success('筛选已重置');
                }}
              >
                重置筛选
              </Button>
            </div>
          </div>
        </div>
        
        <Table
        dataSource={users}
        columns={userColumns}
        loading={loading}
        rowKey="id"
        scroll={{ x: 1200 }}
        pagination={{ 
          pageSize: 10,
          showSizeChanger: true,
          showQuickJumper: true,
          showTotal: (total) => `共 ${total} 个用户`
        }}
      />
    </Card>
    </>
  );

  // 渲染生成记录内容
  const renderGenerationsContent = () => {
    const filteredGenerations = generations; // 已改为后端筛选

    return (
      <>
        {/* 页面标题 - 美化版 */}
        <div className="flex justify-between items-center mb-6">
          <div>
            <h2 className="text-3xl font-bold mb-2">
              <span className="bg-gradient-to-r from-purple-600 via-pink-600 to-rose-600 bg-clip-text text-transparent">
                🎬 生成记录
              </span>
            </h2>
            <p className="text-gray-600">查看和管理所有视频和图片生成历史</p>
          </div>
          <Button 
            type="primary"
            icon={<ReloadOutlined />}
            onClick={() => {
              fetchGenerations({ page: 1 });
              message.success('生成记录已刷新');
            }}
            size="large"
          >
            刷新数据
          </Button>
        </div>

        <Card className="shadow-xl rounded-2xl border-0">
          <Alert
            message="生成记录说明"
            description="查看所有视频和图片生成记录，可按类型和状态筛选"
            type="info"
            showIcon
            closable
            className="mb-4"
          />

          {/* 统计行：合并显示所有统计信息 */}
          {genSummary && (
            <div className="mb-4 flex gap-4 text-sm items-center flex-wrap">
              <Tag color="geekblue">全部: {genSummary.allCount}</Tag>
              <Tag color="blue">视频: {genSummary.videoTotal}</Tag>
              <Tag color="purple">图片: {genSummary.imageTotal}</Tag>
              <Tooltip title="视频条目数 + 图片条目数，用于总体统计展示">
                <Tag color="processing">全部作品数: {genSummary.allCount}</Tag>
              </Tooltip>
              <Tooltip title="分页记录总数，用于翻页（联合分页后等于全部作品数）">
                <Tag color="gold">列表记录总数: {genTotal}</Tag>
              </Tooltip>
            </div>
          )}
          
          {/* 筛选器 */}
        <div className="mb-4 flex flex-wrap gap-4 items-center">
          <div className="flex items-center gap-2">
            <span className="text-gray-600 font-medium">类型:</span>
            <Space.Compact>
              <Button
                type={generationType === 'all' ? 'primary' : 'default'}
                onClick={() => {
                  setGenerationType('all');
                  setGenPage(1);
                  fetchGenerations({ type: 'all', page: 1 });
                }}
              >
                全部
              </Button>
              <Button
                type={generationType === 'video' ? 'primary' : 'default'}
                icon={<VideoCameraOutlined />}
                onClick={() => {
                  setGenerationType('video');
                  setGenPage(1);
                  fetchGenerations({ type: 'video', page: 1 });
                }}
              >
                视频
              </Button>
              <Button
                type={generationType === 'image' ? 'primary' : 'default'}
                icon={<PictureOutlined />}
                onClick={() => {
                  setGenerationType('image');
                  setGenPage(1);
                  fetchGenerations({ type: 'image', page: 1 });
                }}
              >
                图片
              </Button>
            </Space.Compact>
          </div>
          
          {/* 按用户筛选 */}
          <div className="flex items-center gap-2">
            <span className="text-gray-600 font-medium">用户:</span>
            <Select
              allowClear
              showSearch
              placeholder="按用户筛选"
              style={{ minWidth: 220 }}
              filterOption={(input, option) =>
                ((option?.label as string) || '').toLowerCase().includes(input.toLowerCase())
              }
              options={(users || []).map(u => ({
                value: u.id,
                label: `${u.username || u.email} (${u.email})`
              }))}
              value={generationUserId}
              onChange={(val) => {
                setGenerationUserId(val);
                setGenPage(1);
                fetchGenerations({ userId: val || undefined, page: 1 });
              }}
            />
          </div>

          <div className="flex items-center gap-2">
            <span className="text-gray-600 font-medium">状态:</span>
            <Space.Compact>
              <Button
                type={generationStatus === 'all' ? 'primary' : 'default'}
                onClick={() => {
                  setGenerationStatus('all');
                  setGenPage(1);
                  fetchGenerations({ status: 'all', page: 1 });
                }}
                size="small"
              >
                全部
              </Button>
              <Button
                type={generationStatus === 'PENDING' ? 'primary' : 'default'}
                onClick={() => {
                  setGenerationStatus('PENDING');
                  setGenPage(1);
                  fetchGenerations({ status: 'PENDING', page: 1 });
                }}
                size="small"
              >
                待处理
              </Button>
              <Button
                type={generationStatus === 'PROCESSING' ? 'primary' : 'default'}
                onClick={() => {
                  setGenerationStatus('PROCESSING');
                  setGenPage(1);
                  fetchGenerations({ status: 'PROCESSING', page: 1 });
                }}
                size="small"
              >
                处理中
              </Button>
              <Button
                type={generationStatus === 'COMPLETED' ? 'primary' : 'default'}
                onClick={() => {
                  setGenerationStatus('COMPLETED');
                  setGenPage(1);
                  fetchGenerations({ status: 'COMPLETED', page: 1 });
                }}
                size="small"
              >
                已完成
              </Button>
              <Button
                type={generationStatus === 'FAILED' ? 'primary' : 'default'}
                onClick={() => {
                  setGenerationStatus('FAILED');
                  setGenPage(1);
                  fetchGenerations({ status: 'FAILED', page: 1 });
                }}
                size="small"
              >
                失败
              </Button>
              <Button
                type={generationStatus === 'CANCELLED' ? 'primary' : 'default'}
                onClick={() => {
                  setGenerationStatus('CANCELLED');
                  setGenPage(1);
                  fetchGenerations({ status: 'CANCELLED', page: 1 });
                }}
                size="small"
              >
                已取消
              </Button>
            </Space.Compact>
          </div>
          
          <Button
            onClick={() => {
              setGenerationType('all');
              setGenerationStatus('all');
              setGenerationUserId(undefined);
              setGenPage(1);
              setGenLimit(10);
              fetchGenerations({ type: 'all', status: 'all', userId: undefined, page: 1, limit: 10 });
            }}
            icon={<ReloadOutlined />}
          >
            重置
          </Button>
        </div>
        
        <Table
          dataSource={filteredGenerations}
          columns={generationColumns}
          loading={loading}
          rowKey="id"
          scroll={{ x: 1000 }}
          pagination={{ 
            current: genPage,
            pageSize: genLimit,
            total: genTotal,
            showSizeChanger: true,
            pageSizeOptions: [10, 20],
            showQuickJumper: true,
            showTotal: (total) => `共 ${total} 条记录`,
            onChange: (page, pageSize) => {
              const totalPages = Math.max(1, Math.ceil((genTotal || 0) / (pageSize || genLimit)));
              const nextPage = Math.min(Math.max(1, page), totalPages);
              setGenPage(nextPage);
              setGenLimit(pageSize);
              fetchGenerations({ page: nextPage, limit: pageSize });
            },
            onShowSizeChange: (page, pageSize) => {
              const totalPages = Math.max(1, Math.ceil((genTotal || 0) / (pageSize || genLimit)));
              const nextPage = Math.min(1, totalPages); // 重置到第一页
              setGenPage(nextPage);
              setGenLimit(pageSize);
              fetchGenerations({ page: nextPage, limit: pageSize });
            }
          }}
        />
      </Card>
      </>
    );
  };

  // 渲染支付记录内容
  const renderPaymentsContent = () => {
    // 筛选支付记录
    const filteredPayments = payments.filter(item => {
      const typeMatch = paymentType === 'all' || item.type === paymentType;
      const statusMatch = paymentStatus === 'all' || item.status === paymentStatus;
      return typeMatch && statusMatch;
    });

    return (
      <>
        {/* 页面标题 - 美化版 */}
        <div className="flex justify-between items-center mb-6">
          <div>
            <h2 className="text-3xl font-bold mb-2">
              <span className="bg-gradient-to-r from-green-600 via-emerald-600 to-teal-600 bg-clip-text text-transparent">
                💰 支付记录
              </span>
            </h2>
            <p className="text-gray-600">查看所有支付交易记录和财务统计</p>
          </div>
          <Button 
            type="primary"
            icon={<ReloadOutlined />}
            onClick={() => {
              fetchPayments();
              message.success('支付记录已刷新');
            }}
            size="large"
          >
            刷新数据
          </Button>
        </div>

        <Card className="shadow-xl rounded-2xl border-0">
          <Alert
            message="支付记录说明"
            description="查看所有支付交易记录和状态，可按类型和状态筛选"
            type="info"
            showIcon
            closable
            className="mb-4"
          />
          
          {/* 筛选器 */}
        <div className="mb-4 flex flex-wrap gap-4 items-center">
          <div className="flex items-center gap-2">
            <span className="text-gray-600 font-medium">类型:</span>
            <Space.Compact>
              <Button
                type={paymentType === 'all' ? 'primary' : 'default'}
                onClick={() => setPaymentType('all')}
              >
                全部
              </Button>
              <Button
                type={paymentType === 'CREDITS' ? 'primary' : 'default'}
                icon={<DollarOutlined />}
                onClick={() => setPaymentType('CREDITS')}
              >
                充值
              </Button>
              <Button
                type={paymentType === 'SUBSCRIPTION' ? 'primary' : 'default'}
                icon={<CrownOutlined />}
                onClick={() => setPaymentType('SUBSCRIPTION')}
              >
                订阅
              </Button>
            </Space.Compact>
          </div>
          
          <div className="flex items-center gap-2">
            <span className="text-gray-600 font-medium">状态:</span>
            <Space.Compact>
              <Button
                type={paymentStatus === 'all' ? 'primary' : 'default'}
                onClick={() => setPaymentStatus('all')}
                size="small"
              >
                全部
              </Button>
              <Button
                type={paymentStatus === 'PENDING' ? 'primary' : 'default'}
                onClick={() => setPaymentStatus('PENDING')}
                size="small"
              >
                待处理
              </Button>
              <Button
                type={paymentStatus === 'COMPLETED' ? 'primary' : 'default'}
                onClick={() => setPaymentStatus('COMPLETED')}
                size="small"
              >
                已完成
              </Button>
              <Button
                type={paymentStatus === 'FAILED' ? 'primary' : 'default'}
                onClick={() => setPaymentStatus('FAILED')}
                size="small"
              >
                失败
              </Button>
            </Space.Compact>
          </div>
          
          <Button
            onClick={() => {
              setPaymentType('all');
              setPaymentStatus('all');
            }}
            icon={<ReloadOutlined />}
          >
            重置
          </Button>
        </div>
        
        <Table
          dataSource={filteredPayments}
          columns={paymentColumns}
          loading={loading}
          rowKey="id"
          scroll={{ x: 900 }}
          pagination={{ 
            pageSize: 10,
            showSizeChanger: true,
            showQuickJumper: true,
            showTotal: (total) => `共 ${total} 条记录`
          }}
        />
      </Card>
      </>
    );
  };

  return (
    <>
      <div className="admin-dashboard min-h-screen bg-gradient-to-br from-gray-50 to-blue-50">
        {/* PC端左侧边栏 */}
        <div className="hidden lg:block">
          <AdminSidebar 
            activeKey={activeMenu} 
            onChange={setActiveMenu}
            collapsed={sidebarCollapsed}
            onCollapse={setSidebarCollapsed}
          />
        </div>
        
        {/* 主内容区域 */}
        <div 
          className={`admin-content min-h-screen transition-all duration-300 ${
            sidebarCollapsed ? 'lg:ml-[80px]' : 'lg:ml-[220px]'
          }`}
        >
          <div className="py-4 px-3 sm:py-6 sm:px-4 lg:px-8">
            <div className="max-w-7xl mx-auto">
              {/* 页面头部 - 仅在概览页面显示 */}
              {activeMenu === 'overview' && (
                <motion.div
                  initial={{ opacity: 0, y: -8 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ duration: 0.3, ease: [0.4, 0, 0.2, 1] }}
                  className="mb-4 sm:mb-6"
                >
                  <Card className="border-0 shadow-lg rounded-xl">
                    <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3 sm:gap-4">
                      <div className="flex items-center space-x-2 sm:space-x-3">
                        <div className="w-10 h-10 sm:w-12 sm:h-12 bg-gradient-to-br from-red-500 to-pink-500 rounded-xl flex items-center justify-center flex-shrink-0">
                          <CrownOutlined className="text-xl sm:text-2xl text-white" />
                        </div>
                        <div>
                          <h1 className="text-xl sm:text-2xl lg:text-3xl font-bold text-gray-800 mb-0 sm:mb-1">管理控制台</h1>
                          <p className="text-xs sm:text-sm lg:text-base text-gray-600 truncate max-w-[200px] sm:max-w-none">
                            欢迎, {user?.email?.split('@')[0]} (管理员)
                          </p>
                        </div>
                      </div>
                      <Space wrap size="small">
                        <Button 
                          icon={<HomeOutlined />} 
                          onClick={() => navigate('/')}
                          size="middle"
                          className="lg:hidden"
                        >
                          返回
                        </Button>
                        <Button 
                          icon={<ReloadOutlined />} 
                          onClick={() => {
                            fetchStats();
                            fetchUsers();
                            fetchGenerations();
                            fetchPayments();
                          }}
                          size="middle"
                        >
                          刷新
                        </Button>
                      </Space>
                    </div>
                  </Card>
                </motion.div>
              )}

              {/* 移动端标签导航 */}
              <div className="lg:hidden mb-4">
                <Card className="border-0 shadow-md rounded-lg overflow-x-auto">
                  <Tabs 
                    activeKey={activeMenu}
                    onChange={(key) => {
                      setActiveMenu(key);
                      // 切换时自动加载数据
                      if (key === 'generations' && generations.length === 0) {
                        fetchGenerations();
                      }
                      if (key === 'payments' && payments.length === 0) {
                        fetchPayments();
                      }
                    }}
                    size="small"
                    type="card"
                    items={[
                      { key: 'overview', label: '概览', icon: <DashboardOutlined /> },
                      { key: 'users', label: '用户', icon: <TeamOutlined /> },
                      { key: 'generations', label: '生成', icon: <ApiOutlined /> },
                      { key: 'payments', label: '支付', icon: <DollarOutlined /> },
                      { key: 'settings', label: '配置', icon: <SettingOutlined /> },
                      { key: 'status', label: '状态', icon: <DashboardOutlined /> },
                    ]}
                  />
                </Card>
              </div>

              {/* 主内容区域 */}
              {renderContent()}
            </div>
          </div>
        </div>
      </div>

      {/* 编辑用户模态框 */}
      <Modal
          title={
            <div className="flex items-center space-x-2">
              <EditOutlined />
              <span>编辑用户</span>
            </div>
          }
          open={editUserModal}
          onOk={() => form.submit()}
          onCancel={() => setEditUserModal(false)}
          confirmLoading={loading}
          width={600}
          okText="保存"
          cancelText="取消"
        >
          <Form
            form={form}
            layout="vertical"
            onFinish={handleEditUser}
          >
            <Form.Item name="id" hidden>
              <Input />
            </Form.Item>

            <Form.Item
              label="邮箱"
              name="email"
            >
              <Input disabled prefix={<UserOutlined />} />
            </Form.Item>

            <Form.Item
              label="用户名"
              name="username"
            >
              <Input disabled />
            </Form.Item>

            <Form.Item
              label="用户角色"
              name="role"
              rules={[{ required: true }]}
            >
              <Select size="large">
                <Option value="USER">
                  <Space>
                    <UserOutlined />
                    <span>普通用户</span>
                  </Space>
                </Option>
                <Option value="MODERATOR">
                  <Space>
                    <SafetyCertificateOutlined />
                    <span>协管员</span>
                  </Space>
                </Option>
                <Option value="AGENT_ADMIN">
                  <Space>
                    <IdcardOutlined />
                    <span>代理商管理员</span>
                  </Space>
                </Option>
                <Option value="ADMIN">
                  <Space>
                    <CrownOutlined />
                    <span>管理员</span>
                  </Space>
                </Option>
              </Select>
            </Form.Item>
            <Form.Item
              label="代理商绑定"
              name="agentId"
            >
              <Select
                allowClear
                size="large"
                placeholder="选择代理商（留空表示未绑定）"
                loading={bindAgentLoading && agentOptions.length === 0}
                showSearch
                optionFilterProp="children"
              >
                {agentOptions.map((agent) => (
                  <Option value={agent.id} key={agent.id}>
                    <Space>
                      <TeamOutlined />
                      <span>{agent.name}</span>
                      {agent.code && <Tag color="geekblue">{agent.code}</Tag>}
                      {agent.status && (
                        <Tag color={agent.status === 'ACTIVE' ? 'success' : 'warning'}>
                          {agent.status === 'ACTIVE' ? '激活' : agent.status === 'SUSPENDED' ? '暂停' : '过期'}
                        </Tag>
                      )}
                    </Space>
                  </Option>
                ))}
              </Select>
            </Form.Item>

            <Form.Item
              label="用户积分"
              name="credits"
              rules={[{ required: true, type: 'number', min: 0 }]}
            >
              <InputNumber 
                style={{ width: '100%' }}
                min={0}
                max={999999}
                size="large"
                formatter={value => `${value} 积分`}
                parser={value => value?.replace(' 积分', '') as any}
              />
            </Form.Item>

            <Form.Item
              label="账户状态"
              name="isActive"
              valuePropName="checked"
            >
              <Switch 
                checkedChildren="激活" 
                unCheckedChildren="禁用"
                size="default"
              />
            </Form.Item>
          </Form>
        </Modal>

        {/* 用户详情抽屉 */}
        <Drawer
          title={
            <div className="flex items-center space-x-2">
              <UserOutlined />
              <span>用户详情</span>
            </div>
          }
          placement="right"
          onClose={() => setUserDetailDrawer(false)}
          open={userDetailDrawer}
          width={600}
        >
          {selectedUser && (
            <div className="space-y-6">
              {/* 基本信息 */}
              <Card title="基本信息" size="small" className="shadow-sm">
                <Descriptions column={1} bordered size="small">
                  <Descriptions.Item label="用户ID">{selectedUser.id}</Descriptions.Item>
                  <Descriptions.Item label="邮箱">{selectedUser.email}</Descriptions.Item>
                  <Descriptions.Item label="用户名">{selectedUser.username}</Descriptions.Item>
                  <Descriptions.Item label="姓名">
                    {selectedUser.firstName} {selectedUser.lastName}
                  </Descriptions.Item>
                  <Descriptions.Item label="角色">
                  {renderRoleTag(selectedUser.role)}
                </Descriptions.Item>
                <Descriptions.Item label="代理商">
                  {selectedUser.agent ? (
                    <Space size="small">
                      <TeamOutlined />
                      <span>{selectedUser.agent.name}</span>
                      {selectedUser.agent.code && <Tag color="geekblue">{selectedUser.agent.code}</Tag>}
                      {selectedUser.agent.status && (
                        <Tag color={selectedUser.agent.status === 'ACTIVE' ? 'success' : 'warning'}>
                          {{
                            ACTIVE: '激活',
                            SUSPENDED: '暂停',
                            EXPIRED: '过期'
                          }[selectedUser.agent.status] || selectedUser.agent.status}
                    </Tag>
                      )}
                    </Space>
                  ) : (
                    <span className="text-gray-400">未绑定</span>
                  )}
                  </Descriptions.Item>
                  <Descriptions.Item label="状态">
                    <Tag color={selectedUser.isActive ? 'green' : 'red'}>
                      {selectedUser.isActive ? '激活' : '禁用'}
                    </Tag>
                  </Descriptions.Item>
                </Descriptions>
              </Card>

              {/* 积分信息 */}
              <Card title="积分信息" size="small" className="shadow-sm">
                <Descriptions column={1} bordered size="small">
                  <Descriptions.Item label="当前积分">
                    <Badge 
                      count={selectedUser.pointsBalance ?? selectedUser.credits ?? 0} 
                      overflowCount={Number.MAX_SAFE_INTEGER}
                      showZero 
                      style={{ backgroundColor: 'var(--color-success)' }} 
                    />
                  </Descriptions.Item>
                  <Descriptions.Item label="视频生成">
                    {selectedUser.creationStats?.videoGenerations ?? 0}
                  </Descriptions.Item>
                  <Descriptions.Item label="图片生成">
                    {selectedUser.creationStats?.imageGenerations ?? 0}
                  </Descriptions.Item>
                  <Descriptions.Item label="已用积分">
                    {selectedUser.creationStats?.totalCreditsUsed ?? 0}
                  </Descriptions.Item>
                  <Descriptions.Item label="充值积分">
                    {selectedUser.creationStats?.totalCreditsRecharged ?? 0}
                  </Descriptions.Item>
                </Descriptions>
              </Card>

              {/* 时间信息 */}
              <Card title="时间信息" size="small" className="shadow-sm">
                <Descriptions column={1} bordered size="small">
                  <Descriptions.Item label="注册时间">
                    {new Date(selectedUser.createdAt).toLocaleString('zh-CN')}
                  </Descriptions.Item>
                  <Descriptions.Item label="更新时间">
                    {new Date(selectedUser.updatedAt).toLocaleString('zh-CN')}
                  </Descriptions.Item>
                  <Descriptions.Item label="最后登录">
                    {selectedUser.lastLoginAt ? new Date(selectedUser.lastLoginAt).toLocaleString('zh-CN') : '暂无记录'}
                  </Descriptions.Item>
                </Descriptions>
              </Card>

              {/* 操作按钮 */}
              <div className="flex space-x-2">
                <Button 
                  type="primary" 
                  icon={<EditOutlined />}
                  block
                  onClick={() => {
                    loadAgentOptions();
                    form.setFieldsValue({
                      id: selectedUser.id,
                      email: selectedUser.email,
                      username: selectedUser.username,
                      role: selectedUser.role,
                      credits: selectedUser.credits,
                      isActive: selectedUser.isActive,
                      agentId: selectedUser.agent?.id || null
                    });
                    setUserDetailDrawer(false);
                    setEditUserModal(true);
                  }}
                >
                  编辑用户
                </Button>
                <Popconfirm
                  title={`确定${selectedUser.isActive ? '禁用' : '激活'}该用户吗？`}
                  onConfirm={() => {
                    handleToggleUserStatus(selectedUser.id, selectedUser.isActive);
                    setUserDetailDrawer(false);
                  }}
                  okText="确定"
                  cancelText="取消"
                >
                  <Button 
                    danger={selectedUser.isActive}
                    icon={selectedUser.isActive ? <CloseCircleOutlined /> : <CheckCircleOutlined />}
                    block
                  >
                    {selectedUser.isActive ? '禁用用户' : '激活用户'}
                  </Button>
                </Popconfirm>
              </div>
            </div>
          )}
        </Drawer>

        <Modal
          title={
            <Space>
              <LinkOutlined />
              <span>{bindAgentUser?.agent ? '更换代理商' : '绑定代理商'}</span>
            </Space>
          }
          open={bindAgentModal}
          onCancel={() => {
            setBindAgentModal(false);
            setBindAgentUser(null);
          }}
          onOk={handleBindAgent}
          confirmLoading={bindAgentLoading}
          destroyOnHidden
          okText="确认绑定"
          cancelText="取消"
        >
          <Form form={bindAgentForm} layout="vertical" initialValues={{ role: 'AGENT_ADMIN' }}>
            <Form.Item
              label="选择代理商"
              name="agentId"
              rules={[{ required: true, message: '请选择代理商' }]}
            >
              <Select
                placeholder="请选择要绑定的代理商"
                loading={bindAgentLoading && agentOptions.length === 0}
                showSearch
                optionFilterProp="children"
              >
                {agentOptions.map((agent) => (
                  <Option value={agent.id} key={agent.id}>
                    <Space>
                      <TeamOutlined />
                      <span>{agent.name}</span>
                      {agent.code && <Tag color="geekblue">{agent.code}</Tag>}
                      {agent.status && (
                        <Tag color={agent.status === 'ACTIVE' ? 'success' : 'warning'}>{agent.status}</Tag>
                      )}
                    </Space>
                  </Option>
                ))}
              </Select>
            </Form.Item>
            <Form.Item
              label="绑定后角色"
              name="role"
              rules={[{ required: true, message: '请选择角色' }]}
            >
              <Select>
                <Option value="AGENT_ADMIN">
                  <Space>
                    <IdcardOutlined />
                    <span>代理商管理员</span>
                  </Space>
                </Option>
                <Option value="ADMIN">
                  <Space>
                    <CrownOutlined />
                    <span>管理员</span>
                  </Space>
                </Option>
              </Select>
            </Form.Item>
          </Form>
        </Modal>

        {/* 统一的预览Modal（视频和图片） */}
        <Modal
          open={previewModal.visible}
          onCancel={() => setPreviewModal({ visible: false, type: 'image', url: '', urls: [], prompt: '' })}
          footer={null}
          width="80vw"
          centered
          destroyOnHidden
          title={
            <div className="text-center">
              <span className="text-lg font-bold">{previewModal.prompt}</span>
            </div>
          }
        >
          <div className="text-center">
            {previewModal.type === 'video' ? (
              // 视频预览
              <div>
                <video
                  controls
                  autoPlay
                  style={{
                    width: '100%',
                    maxHeight: '70vh',
                    borderRadius: '8px',
                    backgroundColor: 'var(--color-text-primary)'
                  }}
                >
                  <source src={previewModal.url} type="video/mp4" />
                  您的浏览器不支持视频播放。
                </video>
              </div>
            ) : (
              // 图片预览（单张或多张）
              <div>
                {previewModal.urls.length === 1 ? (
                  // 单张图片
                  <img
                    src={previewModal.urls[0]}
                    alt="预览图片"
                    style={{
                      maxWidth: '100%',
                      maxHeight: '70vh',
                      borderRadius: '8px',
                      display: 'block',
                      margin: '0 auto'
                    }}
                    onError={(e) => {
                      const target = e.target as HTMLImageElement;
                      target.src = 'data:image/svg+xml,%3Csvg xmlns="http://www.w3.org/2000/svg" width="400" height="300"%3E%3Crect fill="%23f3f4f6" width="400" height="300"/%3E%3Ctext x="50%25" y="50%25" dominant-baseline="middle" text-anchor="middle" fill="%239ca3af" font-size="16"%3E图片加载失败%3C/text%3E%3C/svg%3E';
                    }}
                  />
                ) : (
                  // 多张图片 - 横向滚动
                  <div>
                    <div className="flex gap-3 justify-center overflow-x-auto pb-4" style={{ maxWidth: '100%' }}>
                      {previewModal.urls.map((url, index) => (
                        <img
                          key={index}
                          src={url}
                          alt={`图片 ${index + 1}`}
                          style={{
                            maxHeight: '60vh',
                            borderRadius: '8px',
                            border: '2px solid var(--color-border, #e5e7eb)',
                            cursor: 'pointer'
                          }}
                          onClick={(e) => {
                            // 点击切换为主图
                            const newUrls = [...previewModal.urls];
                            [newUrls[0], newUrls[index]] = [newUrls[index], newUrls[0]];
                            setPreviewModal({ ...previewModal, urls: newUrls, url: newUrls[0] });
                          }}
                          onError={(e) => {
                            const target = e.target as HTMLImageElement;
                            target.style.display = 'none';
                          }}
                        />
                      ))}
                    </div>
                    <div className="text-gray-500 text-sm mb-2">
                      共 {previewModal.urls.length} 张图片，点击切换查看
                    </div>
                  </div>
                )}
              </div>
            )}
            
            {/* 底部操作按钮 - 明显居中显示 */}
            <div className="mt-6 pt-4 border-t-2 border-gray-200">
              <Space size="large">
                <Button 
                  type="primary" 
                  size="large"
                  icon={<DownloadOutlined />}
                  onClick={async () => {
                    if (previewModal.type === 'video') {
                      // 下载视频
                      const link = document.createElement('a');
                      link.href = previewModal.url;
                      link.download = `${previewModal.prompt}.mp4`;
                      link.target = '_blank';
                      document.body.appendChild(link);
                      link.click();
                      document.body.removeChild(link);
                      message.success('视频下载已开始');
                    } else {
                      // 下载所有图片
                      message.loading('正在准备下载图片...', 0);
                      
                      for (let index = 0; index < previewModal.urls.length; index++) {
                        const url = previewModal.urls[index];
                        try {
                          // 使用fetch下载
                          const response = await fetch(url);
                          const blob = await response.blob();
                          const blobUrl = window.URL.createObjectURL(blob);
                          
                          const link = document.createElement('a');
                          link.href = blobUrl;
                          link.download = `${previewModal.prompt}_${index + 1}.jpg`;
                          link.style.display = 'none';
                          
                          document.body.appendChild(link);
                          link.click();
                          document.body.removeChild(link);
                          
                          window.URL.revokeObjectURL(blobUrl);
                          
                          // 延迟避免浏览器拦截
                          if (index < previewModal.urls.length - 1) {
                            await new Promise(resolve => setTimeout(resolve, 500));
                          }
                        } catch (error) {
                          console.error('下载失败:', error);
                          // 降级：打开新窗口
                          window.open(url, '_blank');
                        }
                      }
                      
                      message.destroy();
                      message.success(`已下载 ${previewModal.urls.length} 张图片`);
                    }
                  }}
                  style={{
                    height: '44px',
                    fontSize: 'var(--font-h4-mobile)',
                    fontWeight: 'bold',
                    minWidth: '120px'
                  }}
                >
                  {previewModal.type === 'video' ? '下载视频' : '下载图片'}
                </Button>
                <Button 
                  size="large"
                  onClick={() => setPreviewModal({ visible: false, type: 'image', url: '', urls: [], prompt: '' })}
                  style={{
                    height: '44px',
                    fontSize: 'var(--font-h4-mobile)',
                    fontWeight: 'bold',
                    minWidth: '120px',
                    borderColor: '#d9d9d9',
                    color: '#595959'
                  }}
                >
                  关闭
                </Button>
              </Space>
            </div>
          </div>
        </Modal>
    </>
  );
};

export default AdminDashboard;

